import { useState } from "react";
import { Box, Flex, Text, Icon } from "@chakra-ui/react";
import { FaUser, FaUsers, FaTimes } from "react-icons/fa";

export type ServiceKind = "egyeni" | "csoportos";

type ServiceTypeModalProps = {
  onCancel: () => void;
  onContinue: (kind: ServiceKind) => void;
};

const TYPES: { key: ServiceKind; icon: typeof FaUser; title: string; desc: string }[] = [
  {
    key: "egyeni",
    icon: FaUser,
    title: "Egyéni szolgáltatás",
    desc: "Csak egy fő vehet részt egyszerre. Például: egyéni konzultáció, masszázs, magánoktatás, személyi edzés stb.",
  },
  {
    key: "csoportos",
    icon: FaUsers,
    title: "Csoportos szolgáltatás",
    desc: "Több személy is részt vehet egyszerre. Például: rendszeres események és alkalmi események.",
  },
];

// Step 1 of "Új szolgáltatás" — pick individual vs. group. A lightweight
// overlay scoped to the mock's own content area (not a Chakra Modal, which
// portals to document.body — wrong here since this whole thing lives
// inside a small framed browser mockup, not the real page).
const ServiceTypeModal = ({ onCancel, onContinue }: ServiceTypeModalProps) => {
  const [picked, setPicked] = useState<ServiceKind | null>(null);

  return (
    <Flex
      position="absolute"
      inset={0}
      bg="rgba(20, 30, 30, 0.35)"
      align="center"
      justify="center"
      px={3}
      zIndex={10}
      borderRadius="inherit"
    >
      <Box bg="white" borderRadius="xl" boxShadow="0 20px 50px rgba(0,0,0,0.25)" p={{ base: 3, sm: 4 }} w="100%" maxW="360px">
        <Flex justify="space-between" align="center" mb={3}>
          <Text fontSize="10px" fontWeight="800" color="ink.900">
            Új szolgáltatás
          </Text>
          <Icon as={FaTimes} boxSize="10px" color="slate.500" cursor="pointer" onClick={onCancel} />
        </Flex>

        <Flex gap={2} mb={3}>
          {TYPES.map((t) => (
            <Flex
              key={t.key}
              as="button"
              type="button"
              onClick={() => setPicked(t.key)}
              direction="column"
              align="center"
              textAlign="center"
              flex="1"
              border="1.5px solid"
              borderColor={picked === t.key ? "wobble.600" : "chalk.300"}
              bg={picked === t.key ? "wobble.50" : "white"}
              borderRadius="lg"
              px={2}
              py={3}
              gap={1.5}
              transition="all 0.15s ease"
            >
              <Icon as={t.icon} boxSize="16px" color="wobble.700" />
              <Text fontSize="8px" fontWeight="700" color="ink.900">
                {t.title}
              </Text>
              <Text fontSize="6.5px" color="slate.500" lineHeight="1.5">
                {t.desc}
              </Text>
            </Flex>
          ))}
        </Flex>

        <Flex justify="flex-end" gap={1.5}>
          <Box as="button" type="button" onClick={onCancel} fontSize="8px" fontWeight="700" color="wobble.700" border="1px solid" borderColor="wobble.300" borderRadius="full" px={2.5} py={1.5}>
            Mégse
          </Box>
          <Box
            as="button"
            type="button"
            onClick={() => picked && onContinue(picked)}
            fontSize="8px"
            fontWeight="700"
            color="white"
            bg={picked ? "wobble.600" : "chalk.400"}
            borderRadius="full"
            px={2.5}
            py={1.5}
            cursor={picked ? "pointer" : "not-allowed"}
          >
            Tovább
          </Box>
        </Flex>
      </Box>
    </Flex>
  );
};

export default ServiceTypeModal;
